import { useMap } from '@vis.gl/react-google-maps';
import { memo, useEffect, useRef, useState } from 'react';
import { Cluster, Marker, MarkerClusterer, MarkerClustererEvents, Renderer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { ISiteLocation } from 'src/domain/site/interface';
import { useUpdateGoogleMapStyles } from './useUpdateGoogleMapStyles';
import { useSiteMapStore } from './useSiteMapStore';
import { calculateMarkerColor } from './formatMapStyle';
import { PriceType } from 'src/domain/price-response/interface';
import { PRIMARY_COLOR } from 'src/theme';

// Hover style injected once — avoids two addEventListener calls per cluster element on every render pass
if (typeof document !== 'undefined' && !document.getElementById('cluster-marker-hover')) {
    const style = document.createElement('style');
    style.id = 'cluster-marker-hover';
    style.textContent = '.cluster-marker:hover { transform: scale(1.15); }';
    document.head.appendChild(style);
}

// Template cache keyed by `${size}_${color}` — cloneNode is far cheaper than full element + cssText creation
const clusterContentCache = new Map<string, HTMLElement>();

type ClusteredTreeMarkersProps = {
    sites: ISiteLocation[];
};

const ClusteredMarkersInner = ({ sites }: ClusteredTreeMarkersProps) => {
    const map = useMap();

    const showCarbonIntensityMap = useSiteMapStore(state => state.showCarbonIntensityMap);
    const carbonIntensity = useSiteMapStore(state => state.carbonIntensity);
    const showLocalMwMap = useSiteMapStore(state => state.showLocalMwMap);
    const priceTypeValue = useSiteMapStore(state => state.priceTypeValue);
    const setMapLocation = useSiteMapStore(state => state.setMapLocation);
    const setSelectedLocationKey = useSiteMapStore(state => state.setSelectedLocationKey);
    const setMarkersLoading = useSiteMapStore(state => state.setMarkersLoading);

    // Only markers within the extended viewport bounds are alive in this map.
    // Markers outside are destroyed (map=null + removed from clusterer), keeping the DOM
    // under ~100 AdvancedMarkerElement nodes regardless of total dataset size.
    const markersRef = useRef<{ [key: string]: google.maps.marker.AdvancedMarkerElement }>({});
    const renderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Stable refs so the idle listener closure reads current values without being recreated
    const sitesRef = useRef(sites);
    const showLocalMwMapRef = useRef(showLocalMwMap);
    const priceTypeValueRef = useRef(priceTypeValue);
    const syncMarkersRef = useRef<(() => void) | null>(null);
    const sitePriceMap = useRef(new Map<string, { rt: number; da: number }>());

    const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);
    const clustererRef = useRef<MarkerClusterer | null>(null);

    // Keep refs current; re-trigger bounds sync when sites data changes
    useEffect(() => {
        sitesRef.current = sites;
        sitePriceMap.current = makePricesMap(sites);
        syncMarkersRef.current?.();
    }, [sites]);

    // Update visible marker colors and re-render clusters when display options change.
    // Uses refs so the clusterer doesn't need to be recreated on every toggle.
    useEffect(() => {
        showLocalMwMapRef.current = showLocalMwMap;
        priceTypeValueRef.current = priceTypeValue;

        for (const [key, marker] of Object.entries(markersRef.current)) {
            const prices = sitePriceMap.current.get(key);
            const price = prices ? (priceTypeValue === PriceType.REAL_TIME ? prices.rt : prices.da) : 0;
            const color = showLocalMwMap ? calculateMarkerColor(price) : PRIMARY_COLOR;
            (marker.content as HTMLElement).style.background = color;
        }

        if (renderTimerRef.current) clearTimeout(renderTimerRef.current);
        renderTimerRef.current = setTimeout(() => {
            clustererRef.current?.render();
            renderTimerRef.current = null;
        }, 100);
    }, [showLocalMwMap, priceTypeValue]);

    // Renderer reads display options from refs — created once per map instance,
    // no need to recreate when showLocalMwMap / priceTypeValue change.
    useEffect(() => {
        if (!map) return;

        const renderer: Renderer = {
            render({ count, position, markers: clusterMarkers }: Cluster): google.maps.marker.AdvancedMarkerElement {
                const markersInCluster = clusterMarkers || [];
                const color = getClusterColor(markersInCluster, showLocalMwMapRef.current, sitePriceMap.current, priceTypeValueRef.current);
                const size = count < 10 ? 36 : count < 100 ? 44 : 56;
                const element = getClusterContent(size, color, String(count));
                return new google.maps.marker.AdvancedMarkerElement({
                    position,
                    content: element,
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
            },
        };

        const instance = new MarkerClusterer({
            map,
            renderer,
            algorithm: new SuperClusterAlgorithm({
                maxZoom: 17, // cluster through zoom 17; individual markers only appear at 18–19 (map maxZoom is 19)
            }),
        });

        const clusteringEndListener = instance.addListener(MarkerClustererEvents.CLUSTERING_END, () => {
            setMarkersLoading(false);
        });

        clustererRef.current = instance;
        setClusterer(instance);

        return () => {
            google.maps.event.removeListener(clusteringEndListener);
            instance.clearMarkers();
            clustererRef.current = null;
        };
    }, [map]);

    // Core viewport sync: creates AdvancedMarkerElement instances only for sites within the
    // extended map bounds, destroys them when they leave. Re-runs on idle so the active
    // marker set always reflects what's visible. This caps live DOM nodes to ~50-100
    // instead of the full dataset, eliminating the forced-reflow cost that scaled with size.
    useEffect(() => {
        if (!map || !clusterer) return;

        const syncMarkersForBounds = () => {
            const bounds = map.getBounds();
            if (!bounds) return;

            const extended = extendBounds(bounds, 0.3);

            // First unique location within extended bounds wins; co-located sites share one marker
            const inBoundsGroups = new Map<string, ISiteLocation>();
            for (const site of sitesRef.current) {
                if (site.site_lat == null || site.site_long == null) continue;
                const key = `${site.site_lat}_${site.site_long}`;
                if (!inBoundsGroups.has(key) && extended.contains({ lat: site.site_lat!, lng: site.site_long! })) {
                    inBoundsGroups.set(key, site);
                }
            }

            // Destroy markers that left the extended bounds
            const markersToRemove: google.maps.marker.AdvancedMarkerElement[] = [];
            for (const key of Object.keys(markersRef.current)) {
                if (!inBoundsGroups.has(key)) {
                    markersToRemove.push(markersRef.current[key]);
                    markersRef.current[key].map = null;
                    delete markersRef.current[key];
                }
            }
            if (markersToRemove.length > 0) clusterer.removeMarkers(markersToRemove, true);

            // Create markers for newly in-bounds locations
            const markersToAdd: google.maps.marker.AdvancedMarkerElement[] = [];
            for (const [key, site] of inBoundsGroups) {
                if (markersRef.current[key]) continue;

                const prices = sitePriceMap.current.get(key);
                const price = prices ? (priceTypeValueRef.current === PriceType.REAL_TIME ? prices.rt : prices.da) : 0;
                const color = showLocalMwMapRef.current ? calculateMarkerColor(price) : PRIMARY_COLOR;
                const content = makeSiteMarkerElement(color);
                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: { lat: site.site_lat!, lng: site.site_long! },
                    content,
                });
                marker.addListener('click', (e: google.maps.MapMouseEvent) => {
                    e.stop();
                    setSelectedLocationKey(key);
                    setMapLocation({ lat: site.site_lat, lng: site.site_long });
                });
                markersRef.current[key] = marker;
                markersToAdd.push(marker);
            }
            if (markersToAdd.length > 0) clusterer.addMarkers(markersToAdd, true);

            if (markersToRemove.length > 0 || markersToAdd.length > 0) {
                setMarkersLoading(true);

                if (renderTimerRef.current) clearTimeout(renderTimerRef.current);
                renderTimerRef.current = setTimeout(() => {
                    clusterer.render();
                    renderTimerRef.current = null;
                }, 100);
            }
        };

        syncMarkersRef.current = syncMarkersForBounds;
        syncMarkersForBounds();

        const idleListener = map.addListener('idle', syncMarkersForBounds);

        return () => {
            google.maps.event.removeListener(idleListener);
            syncMarkersRef.current = null;
            for (const key of Object.keys(markersRef.current)) {
                markersRef.current[key].map = null;
                delete markersRef.current[key];
            }
        };
    }, [map, clusterer]);

    useEffect(() => {
        if (!map || !sites.length) return;

        const bounds = new google.maps.LatLngBounds();
        sites.forEach(site => bounds.extend(new google.maps.LatLng(site.site_lat!, site.site_long!)));
        map.fitBounds(bounds);

        const mapCenter = map.getCenter()?.toJSON() as google.maps.LatLngLiteral;
        if (mapCenter) {
            setMapLocation({ ...mapCenter });
        }
    }, [map, sites]);

    useEffect(() => {
        if (!map) return;

        const listener = map.addListener('dragend', () => {
            const mapCenter = map.getCenter()?.toJSON() as google.maps.LatLngLiteral;
            if (mapCenter) {
                setMapLocation({ ...mapCenter });
            }
        });

        return () => google.maps.event.removeListener(listener);
    }, [map]);

    useUpdateGoogleMapStyles(carbonIntensity, showCarbonIntensityMap);

    return null;
};

export const ClusteredMarkers = memo(ClusteredMarkersInner, (prev, next) => prev.sites === next.sites);

function makeSiteMarkerElement(color: string): HTMLElement {
    const div = document.createElement('div');
    div.style.cssText = `width:15px;height:15px;border-radius:50%;background:${color};border:1px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);cursor:pointer;`;
    return div;
}

function makePricesMap(sites: ISiteLocation[]) {
    const sitePriceMap = new Map<string, { rt: number; da: number }>();
    sites.forEach(site => sitePriceMap.set(`${site.site_lat}_${site.site_long}`, { rt: site.lmp_price_rt || 0, da: site.lmp_price_da || 0 }));
    return sitePriceMap;
}

// Extends bounds by `factor` in each direction — provides a scroll buffer so markers
// are pre-created just outside the visible edge, preventing pop-in when panning slowly.
function extendBounds(bounds: google.maps.LatLngBounds, factor: number): google.maps.LatLngBounds {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latPad = (ne.lat() - sw.lat()) * factor;
    const lngPad = (ne.lng() - sw.lng()) * factor;
    return new google.maps.LatLngBounds({ lat: sw.lat() - latPad, lng: sw.lng() - lngPad }, { lat: ne.lat() + latPad, lng: ne.lng() + lngPad });
}

function getClusterColor(
    markersInCluster: Marker[],
    showLocalMwMap: boolean,
    sitePriceMap: Map<string, { rt: number; da: number }>,
    priceTypeValue: PriceType
) {
    if (!showLocalMwMap) return PRIMARY_COLOR;

    const totalPrice = markersInCluster.reduce((prevValue, marker) => {
        // @ts-ignore
        const key = `${marker.position.lat}_${marker.position.lng}`;
        const price = sitePriceMap.get(key) || { rt: 0, da: 0 };
        return prevValue + (priceTypeValue === PriceType.REAL_TIME ? price.rt : price.da);
    }, 0);

    const avgClusterPrice = totalPrice / markersInCluster.length;
    return calculateMarkerColor(avgClusterPrice);
}

function getClusterContent(size: number, color: string, title: string): HTMLElement {
    const cacheKey = `${size}_${color}`;
    let template = clusterContentCache.get(cacheKey);
    if (!template) {
        template = makeClusterTemplate(size, color);
        clusterContentCache.set(cacheKey, template);
    }
    const el = template.cloneNode(false) as HTMLElement;
    el.textContent = title;
    return el;
}

function makeClusterTemplate(size: number, color: string): HTMLElement {
    const div = document.createElement('div');
    div.className = 'cluster-marker';
    div.style.cssText = `
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background: ${color};
        border: 1px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${size < 44 ? 12 : 14}px;
        font-family: Arial, sans-serif;
        cursor: pointer;
        transition: transform 0.15s ease;
    `;
    return div;
}
